import type { Block } from '../../../../types/proposal-blocks';
import { renderMarkdown } from './markdown';
import { Copy, Check } from 'lucide-react';
import { useState } from 'react';

type Data = Extract<Block, { type: 'package_page' }>;

export function PackagePageBlock({ block }: { block: Data }) {
  const [copied, setCopied] = useState(false);

  const badgeClass = block.badge === 'recomendado'
    ? 'bg-proposal-accent text-proposal-accent-text'
    : 'bg-proposal-bg4 text-proposal-fg2 border border-proposal-border-strong';

  const handleCopy = () => {
    if (block.yaml_summary) {
      navigator.clipboard.writeText(JSON.stringify(block.yaml_summary, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div id={block.id} className="mb-10">
      {/* Badge */}
      {block.badge && (
        <span className={`inline-flex items-center font-proposal-mono text-[11px] font-semibold tracking-wide px-2.5 py-0.5 rounded-full mb-3.5 ${badgeClass}`}>
          {block.badge === 'recomendado' ? 'RECOMENDADO' : block.badge.toUpperCase()}
        </span>
      )}

      {/* Title + meta */}
      <h2 className="text-[28px] font-bold tracking-tight text-proposal-fg1 leading-tight mb-2">
        {block.name}
      </h2>
      {(block.price || block.hours) && (
        <p className="text-sm text-proposal-fg2 font-proposal-mono mb-6">
          {[block.price, block.hours].filter(Boolean).join(' · ')}
        </p>
      )}

      {/* Tasks table */}
      {block.tasks.length > 0 && (
        <div className="overflow-x-auto my-4 border border-proposal-border rounded-p-lg">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr>
                <th className="bg-proposal-bg2 text-proposal-fg3 text-[11px] font-semibold uppercase tracking-wide px-3.5 py-2.5 text-left border-b border-proposal-border">Tarea</th>
                <th className="bg-proposal-bg2 text-proposal-fg3 text-[11px] font-semibold uppercase tracking-wide px-3.5 py-2.5 text-left border-b border-proposal-border">Descripción</th>
                {block.tasks.some(t => t.hours) && (
                  <th className="bg-proposal-bg2 text-proposal-fg3 text-[11px] font-semibold uppercase tracking-wide px-3.5 py-2.5 text-left border-b border-proposal-border">Horas est.</th>
                )}
              </tr>
            </thead>
            <tbody>
              {block.tasks.map((task, i) => (
                <tr key={i} className="hover:bg-proposal-bg2/50">
                  <td className="px-3.5 py-2.5 border-b border-proposal-border">
                    <span className="inline-block font-proposal-mono text-[11px] font-medium bg-proposal-accent-dim text-proposal-accent border border-[rgba(182,255,60,0.25)] rounded px-2 py-0.5">
                      {task.name}
                    </span>
                  </td>
                  <td className="px-3.5 py-2.5 border-b border-proposal-border text-proposal-fg1">{task.description}</td>
                  {block.tasks.some(t => t.hours) && (
                    <td className="px-3.5 py-2.5 border-b border-proposal-border font-proposal-mono text-[12px] text-proposal-fg2">{task.hours || '—'}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Why */}
      {block.why && (
        <div className="mt-6">
          <h3 className="text-base font-semibold text-proposal-fg1 mb-3">Por qué este paquete</h3>
          {renderMarkdown(block.why)}
        </div>
      )}

      {/* How measured */}
      {block.how_measured && (
        <div className="mt-6">
          <h3 className="text-base font-semibold text-proposal-fg1 mb-3">Cómo se mide</h3>
          {renderMarkdown(block.how_measured)}
        </div>
      )}

      {/* YAML summary */}
      {block.yaml_summary && (
        <div className="relative bg-[#0D1117] border border-proposal-border rounded-p-lg p-5 my-4 overflow-x-auto">
          <button onClick={handleCopy} className="absolute top-3 right-3 bg-proposal-bg4 border border-proposal-border-strong rounded-p-sm text-proposal-fg3 text-[11px] font-proposal-mono px-2.5 py-0.5 cursor-pointer hover:text-proposal-fg1">
            {copied ? <><Check size={12} className="inline" /> Copiado</> : <><Copy size={12} className="inline" /> Copiar</>}
          </button>
          <pre className="font-proposal-mono text-[13px] leading-[1.7] text-[#e6edf3] whitespace-pre">
            {Object.entries(block.yaml_summary).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join('\n')}
          </pre>
        </div>
      )}
    </div>
  );
}
